import React, { useState } from 'react';
import { FileText, Download, Eye, X, FileImage, File } from 'lucide-react';

const AttachmentViewer = ({ attachment, baseUrl = 'https://www.secs.gismac.co.ke' }) => {
  const [previewOpen, setPreviewOpen] = useState(false);

  if (!attachment) return null;

  // Handle different attachment formats
  let fileUrl = '';
  let fileName = '';
  let fileType = '';

  if (typeof attachment === 'string') {
    // Simple string path like /uploads/incidents/file.jpg
    fileUrl = attachment.startsWith('http') ? attachment : `${baseUrl}${attachment}`;
    fileName = attachment.split('/').pop();
    fileType = fileName.split('.').pop().toLowerCase();
  } else if (attachment.url || attachment.fileUrl || attachment.documentUrl) {
    // Object format
    fileUrl = attachment.url || attachment.fileUrl || attachment.documentUrl;
    if (!fileUrl.startsWith('http')) fileUrl = `${baseUrl}${fileUrl}`;
    fileName = attachment.name || attachment.fileName || attachment.documentName || fileUrl.split('/').pop();
    fileType = (attachment.type || fileName.split('.').pop()).toLowerCase();
  } else if (attachment.name) {
    fileName = attachment.name;
    fileType = fileName.split('.').pop().toLowerCase();
  }

  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(fileType);
  const isPDF = fileType === 'pdf';
  const isDocument = ['doc', 'docx', 'xls', 'xlsx', 'txt', 'csv'].includes(fileType);

  const getFileIcon = () => {
    if (isImage) return <FileImage size={20} />;
    if (isPDF) return <FileText size={20} />;
    return <File size={20} />;
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleView = () => {
    if (isImage || isPDF) {
      setPreviewOpen(true);
    } else {
      window.open(fileUrl, '_blank');
    }
  };

  return (
    <div className="attachment-viewer">
      <div className="attachment-info">
        {getFileIcon()}
        <span className="attachment-name">{fileName}</span>
      </div>
      <div className="attachment-actions">
        <button className="btn-icon" onClick={handleView} title="View">
          <Eye size={16} />
        </button>
        <button className="btn-icon" onClick={handleDownload} title="Download">
          <Download size={16} />
        </button>
      </div>

      {previewOpen && (
        <div className="preview-overlay" onClick={() => setPreviewOpen(false)}>
          <div className="preview-modal" onClick={e => e.stopPropagation()}>
            <div className="preview-header">
              <h4>{fileName}</h4>
              <button className="btn-icon" onClick={() => setPreviewOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="preview-content">
              {isImage ? (
                <img src={fileUrl} alt={fileName} style={{ maxWidth: '100%', maxHeight: '80vh' }} />
              ) : isPDF ? (
                <iframe src={fileUrl} title={fileName} style={{ width: '100%', height: '80vh', border: 'none' }} />
              ) : (
                <div className="preview-fallback">
                  <FileText size={64} />
                  <p>This file type cannot be previewed</p>
                  <button className="btn btn-primary" onClick={handleDownload}>
                    <Download size={16} /> Download File
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .attachment-viewer {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 12px;
          background: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #e0e0e0;
          margin: 8px 0;
        }
        .attachment-info {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
          min-width: 0;
        }
        .attachment-name {
          font-size: 13px;
          color: #333;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .attachment-actions {
          display: flex;
          gap: 4px;
        }
        .btn-icon {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          color: #666;
          transition: all 0.2s;
        }
        .btn-icon:hover {
          background: #e0e0e0;
          color: #1a237e;
        }
        .preview-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.8);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }
        .preview-modal {
          background: white;
          border-radius: 12px;
          max-width: 90vw;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }
        .preview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #eee;
        }
        .preview-header h4 {
          margin: 0;
          font-size: 16px;
          color: #333;
        }
        .preview-content {
          padding: 0;
        }
        .preview-fallback {
          padding: 40px;
          text-align: center;
          color: #666;
        }
      `}</style>
    </div>
  );
};

export default AttachmentViewer;
